import type { LucideIcon } from 'lucide-react';
import './SettingsRows.css';

interface RowShellProps {
    label: string;
    desc: React.ReactNode;
    children: React.ReactNode;
}

function RowShell({ label, desc, children }: RowShellProps): React.JSX.Element {
    return (
        <div className="settingsRow">
            <div className="settingsRowText">
                <div className="settingsRowLabel">{label}</div>
                <div className="settingsRowDesc">{desc}</div>
            </div>
            {children}
        </div>
    );
}

export function Section({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
    return (
        <div className="settingsSection">
            <div className="settingsSectionTitle">{title}</div>
            <div className="settingsSectionBody">{children}</div>
        </div>
    );
}

interface ToggleRowProps {
    label: string;
    desc: string;
    value: boolean;
    onChange: (value: boolean) => void;
}

export function ToggleRow({ label, desc, value, onChange }: ToggleRowProps): React.JSX.Element {
    return (
        <RowShell label={label} desc={desc}>
            <div
                className={`settingsToggleTrack ${value ? 'settingsToggleTrackOn' : ''}`}
                onClick={() => onChange(!value)}
                role="switch"
                aria-checked={value}
            >
                <div className="settingsToggleKnob" />
            </div>
        </RowShell>
    );
}

interface SegmentedRowProps {
    label: string;
    desc: string;
    options: string[];
    value: string;
    onChange: (value: string) => void;
}

export function SegmentedRow({ label, desc, options, value, onChange }: SegmentedRowProps): React.JSX.Element {
    return (
        <RowShell label={label} desc={desc}>
            <div className="settingsSegmented">
                {options.map((option) => (
                    <div
                        key={option}
                        className={`settingsSegmentedOption ${option === value ? 'settingsSegmentedOptionActive' : ''}`}
                        onClick={() => onChange(option)}
                    >
                        {option}
                    </div>
                ))}
            </div>
        </RowShell>
    );
}

interface SliderRowProps {
    label: string;
    desc: string;
    min: number;
    max: number;
    step: number;
    unit: string;
    value: number;
    onChange: (value: number) => void;
}

export function SliderRow({ label, desc, min, max, step, unit, value, onChange }: SliderRowProps): React.JSX.Element {
    return (
        <RowShell label={label} desc={desc}>
            <div className="settingsSlider">
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={value}
                    onChange={(e) => onChange(Number(e.target.value))}
                    className="settingsSliderInput"
                />
                <span className="settingsSliderValue">{value === 0 ? 'Off' : `${value} ${unit}`}</span>
            </div>
        </RowShell>
    );
}

interface TextRowProps {
    label: string;
    desc: string;
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    type?: string;
    placeholder?: string;
    error?: string;
}

export function TextRow({ label, desc, value, onChange, onBlur, type = 'text', placeholder, error }: TextRowProps): React.JSX.Element {
    return (
        <RowShell label={label} desc={error ? <span className="settingsRowDescError">{error}</span> : desc}>
            <div className="settingsTextField">
                <input
                    type={type}
                    value={value}
                    placeholder={placeholder}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onBlur}
                    className="settingsTextInput"
                />
            </div>
        </RowShell>
    );
}

interface ActionRowProps {
    label: string;
    desc: string;
    actionLabel: string;
    tone?: 'default' | 'solid' | 'danger';
    onClick?: () => void;
}

export function ActionRow({ label, desc, actionLabel, tone = 'default', onClick }: ActionRowProps): React.JSX.Element {
    return (
        <RowShell label={label} desc={desc}>
            <div className={`settingsAction settingsAction-${tone}`} onClick={onClick}>
                {actionLabel}
            </div>
        </RowShell>
    );
}

interface DiskUsageRowProps {
    label: string;
    desc: string;
    usedLabel: string;
    freeLabel: string;
    percent: number;
}

export function DiskUsageRow({ label, desc, usedLabel, freeLabel, percent }: DiskUsageRowProps): React.JSX.Element {
    return (
        <RowShell label={label} desc={desc}>
            <div className="settingsDisk">
                <div className="settingsDiskLabels">
                    <span>{usedLabel}</span>
                    <span>{freeLabel}</span>
                </div>
                <div className="settingsDiskTrack">
                    <div className="settingsDiskBar" style={{ width: `${percent}%` }} />
                </div>
            </div>
        </RowShell>
    );
}

interface TableRowProps {
    icon: LucideIcon;
    main: string;
    sub: string;
    tag?: string;
    tagTone?: 'blue' | 'sage' | 'muted';
    actionLabel: string;
    onAction?: () => void;
}

export function TableRow({ icon: Icon, main, sub, tag, tagTone = 'blue', actionLabel, onAction }: TableRowProps): React.JSX.Element {
    return (
        <div className="settingsTableRow">
            <div className="settingsTableIcon">
                <Icon size={17} strokeWidth={2.75} />
            </div>
            <div className="settingsTableText">
                <div className="settingsTableMain">{main}</div>
                <div className="settingsTableSub">{sub}</div>
            </div>
            {tag && <span className={`settingsTag settingsTag-${tagTone}`}>{tag}</span>}
            <div className="settingsTableAction" onClick={onAction}>
                {actionLabel}
            </div>
        </div>
    );
}
